/**
 * useShortcut Hook
 *
 * Register a keyboard shortcut handler with scope awareness.
 *
 * Usage:
 *   useShortcut("approval-approve", () => handleApprove(), {
 *     scope: "approval",
 *     enabled: isOwned,
 *   });
 *
 * The handler will only fire when:
 *   1. The shortcut's scope matches the active scope (or scope is "global")
 *   2. The `enabled` option is true (defaults to true)
 *   3. The shortcut is not suppressed due to input focus
 */

import { useEffect, useRef, useCallback } from "react";
import { useShortcutStore } from "@/shared/stores/shortcut-store";
import {
  getShortcutById,
  matchesShortcut,
  shouldSuppressShortcut,
  isEditableElement,
  type ShortcutScope,
} from "@/shared/lib/shortcuts";

export interface UseShortcutOptions {
  /** The scope in which this shortcut is active. Defaults to the shortcut's defined scope. */
  scope?: ShortcutScope;
  /** Whether the shortcut is currently enabled. Defaults to true. */
  enabled?: boolean;
  /** Whether to prevent default browser behavior. Defaults to true. */
  preventDefault?: boolean;
}

/**
 * Register a keyboard shortcut handler.
 *
 * @param shortcutId - The ID of the shortcut from the canonical map
 * @param handler - The function to call when the shortcut is triggered
 * @param options - Configuration options
 */
export function useShortcut(
  shortcutId: string,
  handler: () => void,
  options: UseShortcutOptions = {},
): void {
  const { enabled = true, preventDefault = true } = options;

  const shortcut = getShortcutById(shortcutId);
  const scope = options.scope ?? shortcut?.scope ?? "global";

  const activeScope = useShortcutStore((state) => state.activeScope);
  const overlayOpen = useShortcutStore((state) => state.overlayOpen);
  const chordPrefix = useShortcutStore((state) => state.chordPrefix);
  const chordPrefixTimestamp = useShortcutStore(
    (state) => state.chordPrefixTimestamp,
  );
  const setChordPrefix = useShortcutStore((state) => state.setChordPrefix);
  const clearChordPrefix = useShortcutStore((state) => state.clearChordPrefix);

  // Keep handler ref stable to avoid re-registering on every render
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't process if no shortcut definition found
      if (!shortcut) return;

      // Don't process if disabled
      if (!enabled) return;

      // Don't process if composing (IME input)
      if (event.isComposing || event.repeat) return;

      // Auto-clear expired chord prefix (1000ms TTL)
      if (chordPrefix && Date.now() - chordPrefixTimestamp > 1000) {
        clearChordPrefix();
      }

      // Cancel chord on Esc, modifiers
      if (
        event.key === "Escape" ||
        (chordPrefix &&
          (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey))
      ) {
        if (chordPrefix) {
          clearChordPrefix();
        }
      }

      // Cancel chord on focus to editable element
      if (chordPrefix && isEditableElement(event.target as Element)) {
        clearChordPrefix();
        return;
      }

      // Detect chord prefix key (single 'g' with no modifiers)
      if (
        event.key === "g" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.shiftKey
      ) {
        // Don't set chord if in an input
        if (!isEditableElement(event.target as Element)) {
          setChordPrefix("g");
          event.preventDefault();
          return;
        }
      }

      // When overlay is open, only process overlay-specific shortcuts
      if (overlayOpen && shortcutId !== "shortcuts-overlay") return;

      // Check if the shortcut matches this event
      if (!matchesShortcut(event, shortcut, chordPrefix)) return;

      // Check scope matching
      // Global shortcuts always fire (unless overlay is open and it's not the overlay toggle)
      // Scoped shortcuts only fire when their scope is active
      const isGlobalShortcut = shortcut.scope === "global";
      const scopeMatches = scope === activeScope || isGlobalShortcut;

      if (!scopeMatches) return;

      // Check if shortcut should be suppressed due to input focus
      if (shouldSuppressShortcut(event, shortcut)) return;

      // Fire the handler
      if (preventDefault) {
        event.preventDefault();
      }
      // Clear chord prefix if this was a chord completion
      if (chordPrefix && shortcut.key.includes(" ")) {
        clearChordPrefix();
      }
      handlerRef.current();
    },
    [
      shortcut,
      shortcutId,
      enabled,
      scope,
      activeScope,
      overlayOpen,
      preventDefault,
      chordPrefix,
      chordPrefixTimestamp,
      setChordPrefix,
      clearChordPrefix,
    ],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Hook to set the active shortcut scope when a component mounts.
 *
 * Usage:
 *   useShortcutScope("queue");
 *
 * The scope will be set when the component mounts.
 */
export function useShortcutScope(scope: ShortcutScope): void {
  const setActiveScope = useShortcutStore((state) => state.setActiveScope);

  useEffect(() => {
    setActiveScope(scope);
  }, [scope, setActiveScope]);
}

/**
 * Hook for modal/dialog components that need to push a scope onto the stack.
 *
 * Unlike useShortcutScope, this properly handles nested contexts by using
 * a scope stack. When the modal closes, the previous scope is restored.
 *
 * Usage:
 *   useShortcutScopePush("rejectDialog", isOpen);
 */
export function useShortcutScopePush(scope: ShortcutScope, active: boolean): void {
  const pushScope = useShortcutStore((state) => state.pushScope);
  const popScope = useShortcutStore((state) => state.popScope);

  useEffect(() => {
    if (active) {
      pushScope(scope);
      return () => popScope();
    }
  }, [active, scope, pushScope, popScope]);
}

export default useShortcut;
